import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { Copy } from "../../components/icons.js";
import "../../styles/public-jobs.css";

interface Share {
  shared: boolean;
  slug: string | null;
  path: string | null;
  url: string | null;
  live: boolean;
  state: string;
}

/**
 * Requirement → "Share publicly". Opt-in per requirement: switching it on mints a public address
 * (company + role), switching it off withdraws it (a later re-share gets a NEW address). The page
 * is only visible while the requirement itself is open; the dialog says so.
 */
export function PublicShareButton({ requisitionId, canEdit }: { requisitionId: string; canEdit: boolean }): ReactNode {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const qc = useQueryClient();
  const key = ["public-share", requisitionId];

  const status = useQuery({
    queryKey: key,
    queryFn: () => api.get<Share>(`/requisitions/${requisitionId}/public-share`),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.put<Share>(`/requisitions/${requisitionId}/public-share`, { enabled }),
    onSuccess: (s) => qc.setQueryData(key, s),
  });

  const s = status.data;
  const link = s?.path ? `${window.location.origin}${s.path}` : null;
  const copy = async (): Promise<void> => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — the link is selectable in the field */
    }
  };

  return (
    <>
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        {s?.shared ? "Shared publicly" : "Share publicly"}
      </button>
      {open && (
        <div className="t1-confirm-scrim" onClick={() => setOpen(false)}>
          <div
            className="card t1-confirm-card"
            role="dialog"
            aria-modal="true"
            aria-label="Share publicly"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-h">
              <h3>Share this requirement publicly</h3>
              <button type="button" className="t1-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="card-b pj-share">
              <p>
                Anyone with the link sees the company, role, location and job description, and can apply. The page
                is live only while this requirement is open — once it is closed or on hold the link says the role is
                no longer open. Interview questions, competencies and scoring are never shown.
              </p>
              <label className="t1-switch">
                <input
                  type="checkbox"
                  checked={Boolean(s?.shared)}
                  disabled={!canEdit || toggle.isPending || status.isLoading}
                  onChange={(e) => toggle.mutate(e.target.checked)}
                />
                <span className="track" />
                <span>{s?.shared ? "Public page is on" : "Public page is off"}</span>
              </label>
              {s?.shared && link && (
                <>
                  <div className="pj-share-link">
                    <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Public link" />
                    <button type="button" className="btn" onClick={copy}>
                      <Copy aria-hidden="true" /> {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  {!s.live && (
                    <p className="pj-warn">
                      This requirement is {s.state.replace(/_/g, " ")}, so the page currently shows “no longer
                      open”.
                    </p>
                  )}
                  <a className="pj-share-open" href={s.path!} target="_blank" rel="noreferrer">
                    Open public page ↗
                  </a>
                </>
              )}
              {toggle.isError && (
                <p className="pj-warn" role="alert">
                  {toggle.error instanceof ApiError ? toggle.error.message : "Could not update sharing."}
                </p>
              )}
              {!canEdit && <p className="pj-muted">Only people who can edit this requirement can change sharing.</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
